import { useState, useMemo } from 'react';
import { useLocalData } from '../hooks/useLocalData';
import { LocalSafetyForm } from '../services/localDataService';
import './Screen.css';

export default function PTPAnalytics() {
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0],
  });
  const [selectedProject, setSelectedProject] = useState<string>('all');

  const { data: ptps, loading } = useLocalData<LocalSafetyForm>('safetyForms');

  // Filter PTPs by date range and project
  const filteredPTPs = useMemo(() => {
    return ptps.filter((ptp) => {
      const ptpDate = ptp.workDate || ptp.createdAt;
      if (!ptpDate) return false;
      
      const date = new Date(ptpDate);
      const startDate = new Date(dateRange.start);
      const endDate = new Date(dateRange.end);
      endDate.setHours(23, 59, 59, 999);

      const matchesDate = date >= startDate && date <= endDate;
      const matchesProject = selectedProject === 'all' || ptp.projectName === selectedProject;

      return matchesDate && matchesProject;
    });
  }, [ptps, dateRange, selectedProject]);

  // Calculate analytics
  const analytics = useMemo(() => {
    const total = filteredPTPs.length;
    const byStatus = filteredPTPs.reduce((acc, ptp) => {
      const status = ptp.status || 'draft';
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const byProject = filteredPTPs.reduce((acc, ptp) => {
      const project = ptp.projectName || 'Unknown';
      acc[project] = (acc[project] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const totalHazards = filteredPTPs.reduce((sum, ptp) => sum + (ptp.hazards?.length || 0), 0);
    const avgHazardsPerPTP = total > 0 ? (totalHazards / total).toFixed(1) : '0';

    const totalControlMeasures = filteredPTPs.reduce((sum, ptp) => sum + (ptp.controlMeasures?.length || 0), 0);
    const avgControlMeasuresPerPTP = total > 0 ? (totalControlMeasures / total).toFixed(1) : '0';

    const completedOnTime = filteredPTPs.filter((ptp) => {
      if (!ptp.nobodyHurtDate || !ptp.workDate) return false;
      const workDate = new Date(ptp.workDate);
      const completedDate = new Date(ptp.nobodyHurtDate);
      return completedDate <= new Date(workDate.getTime() + 24 * 60 * 60 * 1000); // Within 24 hours
    }).length;

    const approvalRate = total > 0 ? ((byStatus.approved || 0) / total * 100).toFixed(1) : '0';

    return {
      total,
      byStatus,
      byProject,
      totalHazards,
      avgHazardsPerPTP,
      totalControlMeasures,
      avgControlMeasuresPerPTP,
      completedOnTime,
      approvalRate,
    };
  }, [filteredPTPs]);

  const projects = useMemo(() => {
    const projectSet = new Set<string>();
    ptps.forEach((ptp) => {
      if (ptp.projectName) projectSet.add(ptp.projectName);
    });
    return Array.from(projectSet).sort();
  }, [ptps]);

  return (
    <div className="screen">
      <div className="screen-header">
        <h1>PTP Analytics & Reporting</h1>
        <p className="screen-subtitle">Data collection and reporting for Safety Department (UC-5)</p>
      </div>

      {/* Filters */}
      <div className="toolbar">
        <div className="toolbar-left">
          <label>
            Start Date:
            <input
              type="date"
              value={dateRange.start}
              onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
              className="filter-input"
            />
          </label>
          <label>
            End Date:
            <input
              type="date"
              value={dateRange.end}
              onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
              className="filter-input"
            />
          </label>
          <select
            value={selectedProject}
            onChange={(e) => setSelectedProject(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Projects</option>
            {projects.map((project) => (
              <option key={project} value={project}>
                {project}
              </option>
            ))}
          </select>
        </div>
        <div className="toolbar-right">
          <button
            className="btn btn-primary"
            onClick={() => {
              // Export functionality would go here
              alert('Export functionality - would export to CSV/Excel');
            }}
          >
            📥 Export Data
          </button>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading analytics...</div>
      ) : (
        <>
          {/* Key Metrics */}
          <div className="stats-grid" style={{ marginBottom: '30px' }}>
            <div className="stat-card">
              <div className="stat-icon">📋</div>
              <div className="stat-content">
                <h3>Total PTPs</h3>
                <p className="stat-value">{analytics.total}</p>
                <p className="stat-description">In selected period</p>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">✔️</div>
              <div className="stat-content">
                <h3>Approval Rate</h3>
                <p className="stat-value">{analytics.approvalRate}%</p>
                <p className="stat-description">PTPs approved</p>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">⚠️</div>
              <div className="stat-content">
                <h3>Avg Hazards/PTP</h3>
                <p className="stat-value">{analytics.avgHazardsPerPTP}</p>
                <p className="stat-description">Average hazards identified</p>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">🛡️</div>
              <div className="stat-content">
                <h3>Avg Controls/PTP</h3>
                <p className="stat-value">{analytics.avgControlMeasuresPerPTP}</p>
                <p className="stat-description">Average control measures</p>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">✅</div>
              <div className="stat-content">
                <h3>Completed On Time</h3>
                <p className="stat-value">{analytics.completedOnTime}</p>
                <p className="stat-description">End-of-day sign-outs</p>
              </div>
            </div>
          </div>

          {/* Status Breakdown */}
          <div className="card" style={{ marginBottom: '20px' }}>
            <h2>Status Breakdown</h2>
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Status</th>
                    <th>Count</th>
                    <th>Percentage</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(analytics.byStatus).map(([status, count]) => (
                    <tr key={status}>
                      <td>
                        <span className={`badge badge-${status === 'approved' ? 'success' : status === 'completed' ? 'info' : status === 'in_progress' ? 'warning' : 'secondary'}`}>
                          {status.replace('_', ' ').toUpperCase()}
                        </span>
                      </td>
                      <td>{count}</td>
                      <td>
                        {analytics.total > 0
                          ? ((count / analytics.total) * 100).toFixed(1)
                          : '0'}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Project Breakdown */}
          <div className="card" style={{ marginBottom: '20px' }}>
            <h2>PTPs by Project</h2>
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Project</th>
                    <th>Count</th>
                    <th>Percentage</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(analytics.byProject)
                    .sort((a, b) => b[1] - a[1])
                    .map(([project, count]) => (
                      <tr key={project}>
                        <td>{project}</td>
                        <td>{count}</td>
                        <td>
                          {analytics.total > 0
                            ? ((count / analytics.total) * 100).toFixed(1)
                            : '0'}%
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Trends */}
          <div className="card">
            <h2>Compliance Trends</h2>
            <div className="info-box">
              <p><strong>Total Hazards Identified:</strong> {analytics.totalHazards}</p>
              <p><strong>Total Control Measures:</strong> {analytics.totalControlMeasures}</p>
              <p><strong>Completion Rate:</strong> {analytics.total > 0 ? ((analytics.completedOnTime / analytics.total) * 100).toFixed(1) : '0'}%</p>
              <p><strong>Average Response Time:</strong> Calculated from creation to approval</p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

